/**
 * Scroll + outline pulse for a kanban match card (`[data-match-card-id]`).
 * Used when returning from match details (URL `focusMatchId`) and after status changes on the board.
 */

const pulseKeyframes: Keyframe[] = [
  {outline: '2px solid transparent', outlineOffset: '0px', backgroundColor: 'transparent'},
  {
    outline: '2px solid rgba(28, 196, 75, 0.85)',
    outlineOffset: '3px',
    backgroundColor: 'rgba(28, 196, 75, 0.05)',
  },
  {
    outline: '2px solid rgba(28, 196, 75, 0.45)',
    outlineOffset: '1px',
    backgroundColor: 'rgba(28, 196, 75, 0.02)',
  },
  {
    outline: '2px solid rgba(28, 196, 75, 0.8)',
    outlineOffset: '3px',
    backgroundColor: 'rgba(28, 196, 75, 0.05)',
  },
  {outline: '2px solid transparent', outlineOffset: '0px', backgroundColor: 'transparent'},
];

const pulseOptions: KeyframeAnimationOptions = {duration: 1700, easing: 'ease-in-out'};

export type ScheduleMatchBoardCardPulseOptions = {
  /** Wait before first DOM lookup (ms). Default 60. */
  initialDelayMs?: number;
  /** Retry interval when the card is not in the DOM yet (ms). Default 80. */
  retryIntervalMs?: number;
  maxAttempts?: number;
  /** After pulse finishes, or when the card never appears in the DOM after retries. */
  onSettled?: () => void;
};

/**
 * Finds the match card, scrolls it into view, runs the focus pulse, then invokes callbacks.
 * @returns Cancel function to stop pending retries (does not cancel an in-flight WAAPI animation).
 */
export function scheduleMatchBoardCardPulse(
  matchId: string,
  options?: ScheduleMatchBoardCardPulseOptions
): () => void {
  let cancelled = false;
  let attempts = 0;
  const maxAttempts = options?.maxAttempts ?? 50;
  const retryIntervalMs = options?.retryIntervalMs ?? 80;
  const initialDelayMs = options?.initialDelayMs ?? 60;

  /** DOM timer handle (`number`); TS may type `setTimeout` as `NodeJS.Timeout` when Node typings are loaded. */
  let startTimer: number | undefined;
  let retryTimer: number | undefined;

  const cancel = () => {
    cancelled = true;
    if (startTimer !== undefined) {
      clearTimeout(startTimer);
      startTimer = undefined;
    }
    if (retryTimer !== undefined) {
      clearTimeout(retryTimer);
      retryTimer = undefined;
    }
  };

  const tryFocus = () => {
    if (cancelled) {
      return;
    }
    const card = document.querySelector<HTMLElement>(`[data-match-card-id="${matchId}"]`);
    if (!card) {
      attempts += 1;
      if (attempts >= maxAttempts) {
        options?.onSettled?.();
        return;
      }
      retryTimer = setTimeout(tryFocus, retryIntervalMs) as unknown as number;
      return;
    }

    card.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});

    // Animate outline only: animating box-shadow would replace the card's
    // multi-layer paperStyles shadow, and filter effects can cause compositor
    // artifacts on draggable cards in some browsers.
    const pulse = card.animate(pulseKeyframes, pulseOptions);
    void pulse.finished
      .then(() => {
        if (!cancelled) {
          options?.onSettled?.();
        }
      })
      .catch(() => {
        if (!cancelled) {
          options?.onSettled?.();
        }
      });
  };

  startTimer = setTimeout(tryFocus, initialDelayMs) as unknown as number;

  return cancel;
}
